import React, { useRef, useEffect, useContext, useState } from "react";
import { SimpleTooltip } from "simple-ui-components";

import SpeakerGroupIcon from "@mui/icons-material/SpeakerGroup";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import DeveloperModeIcon from "@mui/icons-material/DeveloperMode";
import DevicesOutlinedIcon from "@mui/icons-material/DevicesOutlined";

import {
  BodyWrapperStyled,
  BoxStyled,
  CardHeader,
  CardBody,
  CardStyled,
  OuterWrapper,
  EmptyList,
} from "../../theme";
import { BrandDetail } from "./brandDetail";
import { ModelsDetail } from "./modelsDetail";
import { EquipmentsDetail } from "./equipmentsDetail";
import { AddEditBrand } from "./addEditBrand";
import { get, post } from "../../server-requests/serverRequests";
import { AuthContext } from "../authContextProvider";
import DeletePopup from "../shared/deletePopup";

import { del } from "../../server-requests/serverRequests";
import { AddEditModel } from "./addEditModel";
import { LoadingList } from "../shared/loading";
import { AddEditEquipment } from "./addEditEquipment";
import { Layout, useWindowSize } from "../layout/otherPage";
import { EquipmentDetailPopup } from "../dashboard/equipmentDetailPopup";
import { getTranslation, LanguageContext } from "../languageProvider";
import { Log, ReplaceWord } from "../shared/utility";

export const Equipments = () => {
  const addEditBrandRef = useRef();
  const addEditModelRef = useRef();
  const deleteBrandRef = useRef();
  const deleteModelRef = useRef();
  const addEditEquipmentRef = useRef();
  const deleteEquipmentRef = useRef();
  const equipmentDetailRef = useRef();

  const { Tooltip } = SimpleTooltip;

  const { wrapperHeight, boxHeightHalf } = useWindowSize();

  const { snackbarHandler } = useContext(AuthContext);
  const { lang } = useContext(LanguageContext);

  const [reloadBrand, setReloadBrand] = useState(true);
  const [brandList, setBrandList] = useState([]);
  const [loadingBrand, setLoadingBrand] = useState(true);

  const [currentBrand, setCurrentBrand] = useState("");
  const [currentModel, setCurrentModel] = useState("");

  const [reloadModel, setReloadModel] = useState(true);
  const [modelList, setModelList] = useState([]);
  const [loadingModel, setLoadingModel] = useState(true);

  const [reloadEquipment, setReloadEquipment] = useState(true);
  const [equipmentList, setEquipmentList] = useState([]);
  const [equipmentListFull, setEquipmentListFull] = useState([]);
  const [loadingEquipment, setLoadingEquipment] = useState(true);

  // client
  const [clientList, setClientList] = useState([]);

  // states
  const [stateList, setStateList] = useState([]);
  const [stateListTranslated, setStateListTranslated] = useState([]);

  // icon
  const [iconList, setIconList] = useState([]);

  // get Icon List
  useEffect(() => {
    let isMounted = true;
    get("equipments/icons/list")
      .then((icons) => {
        if (isMounted) {
          setIconList(icons);
        }
      })
      .catch((status) => {
        snackbarHandler(getTranslation(`error${status}`));
        Log("Error: get equipments/icons/list" + status);
      });
    return () => (isMounted = false);
  }, [snackbarHandler]);

  // change state list on language change
  useEffect(() => {
    if (lang && stateList.length > 0) {
      let translatedList = stateList.map((item) => ({
        ...item,
        label: getTranslation("state" + item.code),
        color: `#${item.color}`,
      }));
      setStateListTranslated(translatedList);
    }
  }, [lang, stateList]);

  // get State List
  useEffect(() => {
    let isMounted = true;
    get("equipments/states/list")
      .then((state) => {
        if (isMounted) {
          setStateList(state);
        }
      })
      .catch((status) => {
        snackbarHandler(getTranslation(`error${status}`));
        Log("Error: get equipments/states/list" + status);
      });
    return () => (isMounted = false);
  }, [snackbarHandler]);

  // get clients list
  useEffect(() => {
    let isMounted = true;
    get("clients/list")
      .then((client) => {
        if (isMounted) {
          let formattedClient = [];
          if (client.length > 0)
            formattedClient = client.map((item) => ({
              label: item.name,
              companyName: item.company.name,
              ...item,
            }));
          setClientList(formattedClient);
        }
      })
      .catch((status) => {
        snackbarHandler(getTranslation(`error${status}`));
        Log("Error: get clients/list" + status);
      });
    return () => (isMounted = false);
  }, [snackbarHandler]);

  // get Equipments List
  useEffect(() => {
    let isMounted = true;
    if (reloadEquipment) {
      get("equipments/list")
        .then((equipment) => {
          if (isMounted) {
            setEquipmentListFull(equipment);
            setReloadEquipment(false);
          }
        })
        .catch((status) => {
          snackbarHandler(getTranslation(`error${status}`));
          setReloadEquipment(false);
          setLoadingEquipment(false);
          Log("Error: get equipments/list" + status);
        });
    }
    return () => (isMounted = false);
  }, [reloadEquipment, snackbarHandler]);

  // filter Equipments by selected Model
  useEffect(() => {
    if (!reloadEquipment && currentModel) {
      let list = equipmentListFull;
      list = list.filter((item) => item.model.id === currentModel.id);
      setEquipmentList(list);
      setLoadingEquipment(false);
    }
  }, [equipmentListFull, reloadEquipment, currentModel]);

  // get Models List
  useEffect(() => {
    let isMounted = true;
    if (reloadModel) {
      if (currentBrand) {
        get("models/list", {
          brandId: currentBrand.id,
        })
          .then((model) => {
            if (isMounted) {
              setModelList(model);

              if (model.length > 0) {
                setCurrentModel(model[0]);
              } else {
                setCurrentModel("");
                setEquipmentListFull([]);
                setEquipmentList([]);
                setLoadingEquipment(false);
              }

              setReloadModel(false);
              setLoadingModel(false);
            }
          })
          .catch((status) => {
            snackbarHandler(getTranslation(`error${status}`));
            setReloadModel(false);
            setLoadingModel(false);
            Log("Error: get models/list" + status);
          });
      }
    }
    return () => (isMounted = false);
  }, [reloadModel, currentBrand, snackbarHandler]);

  // get Brand List
  useEffect(() => {
    let isMounted = true;
    if (reloadBrand) {
      get("brands/list")
        .then((brand) => {
          if (isMounted) {
            setBrandList(brand);

            if (brand.length > 0) {
              setCurrentBrand(brand[0]);
            } else {
              setCurrentBrand("");
              setModelList([]);
              setEquipmentListFull([]);
              setLoadingModel(false);
              setLoadingEquipment(false);
              setCurrentModel("");
              setEquipmentList([]);
            }
            setReloadModel(true);
            setReloadBrand(false);
            setLoadingBrand(false);
          }
        })
        .catch((status) => {
          snackbarHandler(getTranslation(`error${status}`));
          setReloadBrand(false);
          setLoadingBrand(false);
          Log("Error: get brands/list" + status);
        });
    }
    return () => (isMounted = false);
  }, [reloadBrand, snackbarHandler]);

  const brandAddClickHandler = () =>
    addEditBrandRef.current.AddEditBrandHandler("add");
  const editBrandClickHandler = (brand) =>
    addEditBrandRef.current.AddEditBrandHandler("edit", brand);
  const selectBrandClickHandler = (brand) => {
    setCurrentBrand(brand);
    setCurrentModel("");
    setReloadModel(true);
  };

  const updateBrandSuccessHandler = () => setReloadBrand(true);

  // delete Brand with warning
  const deleteBrandClickHandler = (id) => {
    let name = getTranslation("0086") && getTranslation("0086").toLowerCase();
    deleteBrandRef.current.Delete(
      id,
      `${getTranslation("0032")} ${name}`,
      getTranslation("0152") &&
        ReplaceWord(getTranslation("0152"), "**NAME**", name),
      getTranslation("0151") &&
        ReplaceWord(getTranslation("0151"), "**NAME**", name)
    );
  };

  const deleteBrandHandler = (id) => {
    del("brands", { id })
      .then(() => {
        snackbarHandler(
          getTranslation("0121") &&
            ReplaceWord(
              getTranslation("0121"),
              "**NAME**",
              getTranslation("0086")
            ),
          "success"
        );
        setReloadBrand(true);
      })
      .catch((status) => {
        snackbarHandler(getTranslation(`error${status}`));
        Log("Error: delete brands" + status);
      });
  };

  // add Models
  const addModelClickHandler = () =>
    addEditModelRef.current.AddEditModelHandler("add", currentBrand);
  const editModelClickHandler = (model) =>
    addEditModelRef.current.AddEditModelHandler("edit", currentBrand, model);
  const updateModelSuccessHandler = () => setReloadModel(true);

  // delete Model with warning
  const deleteModelClickHandler = (id) => {
    let name = getTranslation("0085") && getTranslation("0085").toLowerCase();
    deleteModelRef.current.Delete(
      id,
      `${getTranslation("0032")} ${name}`,
      getTranslation("0152") &&
        ReplaceWord(getTranslation("0152"), "**NAME**", name),
      getTranslation("0151") &&
        ReplaceWord(getTranslation("0151"), "**NAME**", name)
    );
  };

  const deleteModelHandler = (id) => {
    del("models", {
      id,
      brandId: currentBrand.id,
    })
      .then(() => {
        snackbarHandler(
          getTranslation("0121") &&
            ReplaceWord(
              getTranslation("0121"),
              "**NAME**",
              getTranslation("0085")
            ),
          "success"
        );
        setReloadModel(true);
      })
      .catch((status) => {
        snackbarHandler(getTranslation(`error${status}`));
        Log("Error: delete models" + status);
      });
  };

  const selectModelClickHandler = (model) => setCurrentModel(model);

  // equipment
  const equipmentAddClickHandler = () =>
    addEditEquipmentRef.current.AddEditEquipmentHandler("add", currentModel);
  const editEquipmentClickHandler = (equipment) =>
    addEditEquipmentRef.current.AddEditEquipmentHandler("edit", equipment);
  const equipmentSelectHandler = (equipment) =>
    equipmentDetailRef.current.Detail(equipment);
  const updateEquipmentSuccessHandler = () => setReloadEquipment(true);

  // delete equipment with warning
  const deleteEquipmentClickHandler = (id) => {
    let name = getTranslation("0071") && getTranslation("0071").toLowerCase();
    deleteEquipmentRef.current.Delete(
      id,
      `${getTranslation("0032")} ${name}`,
      getTranslation("0152") &&
        ReplaceWord(getTranslation("0152"), "**NAME**", name),
      getTranslation("0151") &&
        ReplaceWord(getTranslation("0151"), "**NAME**", name)
    );
  };
  const deleteEquipmentHandler = (id) => {
    del("equipments", { id })
      .then(() => {
        snackbarHandler(
          getTranslation("0121") &&
            ReplaceWord(
              getTranslation("0121"),
              "**NAME**",
              getTranslation("0071")
            ),
          "success"
        );
        setReloadEquipment(true);
      })
      .catch((status) => {
        snackbarHandler(getTranslation(`error${status}`));
        Log("Error: delete equipments" + status);
      });
  };

  const copyEquipmentClickHandler = (equipment) => {
    let previousSerialNumber = equipment.serialNumber;
    let nextSerialNumber = "";
    if (isNaN(previousSerialNumber)) {
      nextSerialNumber = previousSerialNumber + "- Copy";
    } else {
      nextSerialNumber = String(Number(previousSerialNumber) + 1);
      nextSerialNumber = nextSerialNumber.padStart(
        previousSerialNumber.length,
        "0"
      );
    }
    let toSend = {
      serialNumber: nextSerialNumber,
      clientId: equipment.client ? equipment.client.id : null,
      stateId: equipment.state ? equipment.state.id : null,
      icon: equipment.icon.substring(equipment.icon.lastIndexOf("/") + 1),
      latitude: equipment.latitude,
      longitude: (Number(equipment.longitude) + 0.0001).toFixed(8).toString(),
      modelId: equipment.model.id,
    };
    // create Brand
    post("equipments", toSend)
      .then(() => {
        snackbarHandler(
          getTranslation("0122") &&
            ReplaceWord(
              getTranslation("0121"),
              "**NAME**",
              getTranslation("0071")
            ),
          "success"
        );
        setReloadEquipment(true);
      })
      .catch((status) => {
        snackbarHandler(getTranslation(`error${status}`));
        Log("Error: create equipments" + status);
      });
  };

  return (
    <Layout lang={lang}>
      <OuterWrapper height={wrapperHeight}>
        <BodyWrapperStyled>
          <BoxStyled width="md" col="2">
            <CardStyled>
              <CardHeader>
                <SpeakerGroupIcon className="mainIcon" />
                <div className="detail" data-translate="0086">
                  {getTranslation("0086")}
                  <span data-translate="0078, 0086">
                    {getTranslation("0078") &&
                      ReplaceWord(
                        getTranslation("0078"),
                        "**NUMBER**",
                        brandList.length,
                        "**LISTNAME**",
                        getTranslation("0086").toLowerCase()
                      )}
                  </span>
                </div>
                <Tooltip
                  text={`${getTranslation("0087")} ${
                    getTranslation("0086") &&
                    getTranslation("0086").toLowerCase()
                  }`}
                  placement="left"
                >
                  <AddOutlinedIcon onClick={brandAddClickHandler} />
                </Tooltip>
              </CardHeader>
              <CardBody height={boxHeightHalf}>
                {loadingBrand && <LoadingList count={5} />}
                {!loadingBrand && brandList.length === 0 && (
                  <EmptyList data-translate="0086,0123">
                    {getTranslation("0123") &&
                      ReplaceWord(
                        getTranslation("0123"),
                        "**NAME**",
                        getTranslation("0086").toLowerCase()
                      )}
                  </EmptyList>
                )}
                {!loadingBrand &&
                  brandList.length > 0 &&
                  brandList.map((brand, index) => (
                    <BrandDetail
                      key={index}
                      brand={brand}
                      isActive={currentBrand.id === brand.id}
                      Edit={editBrandClickHandler}
                      Select={selectBrandClickHandler}
                      Delete={deleteBrandClickHandler}
                    />
                  ))}
              </CardBody>
            </CardStyled>
          </BoxStyled>
          <BoxStyled width="md" col="2">
            <CardStyled>
              <CardHeader>
                <DeveloperModeIcon className="mainIcon" />
                <div className="detail" data-translate="0085">
                  {getTranslation("0085")}s
                  {currentBrand && (
                    <span data-translate="0080,0085">
                      {getTranslation("0080") &&
                        ReplaceWord(
                          getTranslation("0080"),
                          "**COUNT**",
                          modelList.length,
                          "**NAME1**",
                          getTranslation("0085").toLowerCase() + "s",
                          "**NAME2**",
                          currentBrand.name
                        )}
                    </span>
                  )}
                </div>
                {currentBrand && (
                  <Tooltip
                    text={`${getTranslation("0087")} ${
                      getTranslation("0085") &&
                      getTranslation("0085").toLowerCase()
                    }`}
                    placement="left"
                  >
                    <AddOutlinedIcon onClick={addModelClickHandler} />
                  </Tooltip>
                )}
              </CardHeader>
              <CardBody height={boxHeightHalf}>
                {loadingModel && <LoadingList count={5} />}
                {!loadingModel && modelList.length === 0 && (
                  <EmptyList data-translate="0085,0123">
                    {getTranslation("0123") &&
                      ReplaceWord(
                        getTranslation("0123"),
                        "**NAME**",
                        getTranslation("0085").toLowerCase()
                      )}
                  </EmptyList>
                )}
                {!loadingModel &&
                  modelList.length > 0 &&
                  modelList.map((model, index) => (
                    <ModelsDetail
                      key={index}
                      model={model}
                      isActive={currentModel.id === model.id}
                      Edit={editModelClickHandler}
                      Select={selectModelClickHandler}
                      Delete={deleteModelClickHandler}
                    />
                  ))}
              </CardBody>
            </CardStyled>
          </BoxStyled>
          <BoxStyled noMargin>
            <CardStyled>
              <CardHeader>
                <DevicesOutlinedIcon className="mainIcon" />
                <div className="detail" data-translate="0071">
                  {getTranslation("0071")}s
                  {currentModel && (
                    <span data-translate="0071,0080">
                      {getTranslation("0080") &&
                        ReplaceWord(
                          getTranslation("0080"),
                          "**COUNT**",
                          equipmentList.length,
                          "**NAME1**",
                          getTranslation("0071").toLowerCase() + "s",
                          "**NAME2**",
                          currentModel.name
                        )}
                    </span>
                  )}
                </div>
                {currentModel && (
                  <Tooltip
                    text={`${getTranslation("0087")} ${
                      getTranslation("0071") &&
                      getTranslation("0071").toLowerCase()
                    }`}
                    placement="left"
                  >
                    <AddOutlinedIcon onClick={equipmentAddClickHandler} />
                  </Tooltip>
                )}
              </CardHeader>
              <CardBody height={boxHeightHalf}>
                {loadingEquipment && <LoadingList count={5} />}
                {!loadingEquipment && equipmentList.length === 0 && (
                  <EmptyList datd-translate="0071,0123">
                    {getTranslation("0123") &&
                      ReplaceWord(
                        getTranslation("0123"),
                        "**NAME**",
                        getTranslation("0071").toLowerCase()
                      )}
                  </EmptyList>
                )}
                {!loadingEquipment &&
                  equipmentList.length > 0 &&
                  equipmentList.map((equipment, index) => (
                    <EquipmentsDetail
                      key={index}
                      equipment={equipment}
                      Edit={editEquipmentClickHandler}
                      Delete={deleteEquipmentClickHandler}
                      Copy={copyEquipmentClickHandler}
                      Select={equipmentSelectHandler}
                    />
                  ))}
              </CardBody>
            </CardStyled>
          </BoxStyled>
        </BodyWrapperStyled>
        <AddEditBrand
          ref={addEditBrandRef}
          UpdateSuccess={updateBrandSuccessHandler}
        />
        <AddEditEquipment
          ref={addEditEquipmentRef}
          UpdateSuccess={updateEquipmentSuccessHandler}
          clientList={clientList}
          stateList={stateListTranslated}
          iconList={iconList}
        />
        <AddEditModel
          ref={addEditModelRef}
          UpdateSuccess={updateModelSuccessHandler}
        />
        <DeletePopup ref={deleteBrandRef} DeleteSuccess={deleteBrandHandler} />
        <DeletePopup ref={deleteModelRef} DeleteSuccess={deleteModelHandler} />
        <EquipmentDetailPopup ref={equipmentDetailRef} />
        <DeletePopup
          ref={deleteEquipmentRef}
          DeleteSuccess={deleteEquipmentHandler}
        />
      </OuterWrapper>
    </Layout>
  );
};
